const sectionSelector = '[data-calculator]';
const fieldSelector = '[data-calculator-field]';

function digitsOnly(value) {
    return String(value).replace(/[^0-9]/g, '');
}

// Demo estimate, not an approved business formula.
// Base by handling + up to 12 points for spend + up to 8 points for trips.
// Both are measured between the field limits set in the admin, so the estimate
// does not depend on the currency the spend is entered in.
// Spend grows logarithmically; trips grow linearly. Cap: 80%.
// At the minimum spend / minimum trips / agency the result is 53%.
function estimateSavings(values, handling) {
    const base = { agency: 53, 'travel-manager': 43, independently: 60 };
    const spend = values.travelSpend ? logPosition(values.travelSpend) : 0;
    const trips = values.businessTrips ? linearPosition(values.businessTrips) : 0;

    return Math.min(80, Math.max(0, Math.round((base[handling] ?? 53) + spend * 12 + trips * 8)));
}

function clampPosition(position) {
    return Number.isFinite(position) ? Math.min(1, Math.max(0, position)) : 0;
}

function linearPosition({ value, min, max }) {
    return clampPosition((value - min) / (max - min));
}

function logPosition({ value, min, max }) {
    if (min <= 0) {
        return linearPosition({ value, min, max });
    }

    return clampPosition(Math.log(value / min) / Math.log(max / min));
}

function initField(field, onCommit) {
    const input = field.querySelector('[data-calculator-input]');
    const decrease = field.querySelector('[data-calculator-decrease]');
    const increase = field.querySelector('[data-calculator-increase]');

    if (!input || !decrease || !increase) {
        return;
    }

    const min = Number(input.dataset.min);
    const max = Number(input.dataset.max);
    const step = Number(input.dataset.step);

    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || max < min || step <= 0) {
        return;
    }

    const prefix = input.dataset.prefix || '';
    const suffix = input.dataset.suffix || '';
    const separator = input.dataset.separator || ',';
    const maxSuffix = input.dataset.maxSuffix || '';

    function normalize(rawValue) {
        const digits = digitsOnly(rawValue).replace(/^0+/, '') || '0';
        // Compare length first so even hundreds of digits clamp to max, not Infinity.
        const value = digits.length > String(max).length ? max : Number(digits);
        return Math.min(max, Math.max(min, value));
    }

    function formatDigits(digits) {
        const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

        return prefix + grouped + (digits === String(max) ? maxSuffix : '') + suffix;
    }

    function format(value) {
        return formatDigits(String(value));
    }

    function updateButtons(value) {
        decrease.disabled = value <= min;
        increase.disabled = value >= max;
    }

    function restoreCaret(digitsBeforeCaret) {
        let caret = prefix.length;
        let seen = 0;

        while (caret < input.value.length && seen < digitsBeforeCaret) {
            if (/[0-9]/.test(input.value[caret])) {
                seen += 1;
            }
            caret += 1;
        }
        input.setSelectionRange(caret, caret);
    }

    function formatDraft() {
        const digitsBeforeCaret = digitsOnly(input.value.slice(0, input.selectionStart)).length;

        input.value = formatDigits(digitsOnly(input.value));
        restoreCaret(digitsBeforeCaret);
        updateButtons(normalize(input.value));
        // Formatting only: keep the draft and result unchanged until commit.
    }

    function commit(value = input.value) {
        const normalizedValue = normalize(value);

        input.value = format(normalizedValue);
        updateButtons(normalizedValue);
        input.setAttribute('aria-valuenow', String(normalizedValue));
        input.setAttribute('aria-valuetext', format(normalizedValue));
        onCommit(input.dataset.calculatorInput, { value: normalizedValue, min, max });
    }

    function increment(direction) {
        commit(normalize(input.value) + direction * step);
    }

    input.setAttribute('role', 'spinbutton');
    input.setAttribute('aria-valuemin', String(min));
    input.setAttribute('aria-valuemax', String(max));
    input.addEventListener('beforeinput', (event) => {
        if (event.data && /[^0-9]/.test(event.data) && event.inputType !== 'insertFromPaste') {
            event.preventDefault();
            return;
        }

        const backward = event.inputType === 'deleteContentBackward';
        const forward = event.inputType === 'deleteContentForward';

        if ((backward || forward) && input.selectionStart === input.selectionEnd) {
            // Delete a digit, skipping the immutable currency sign and separators.
            event.preventDefault();
            const caret = input.selectionStart;
            let index = backward ? caret - 1 : caret;

            while (index >= prefix.length && index < input.value.length && !/[0-9]/.test(input.value[index])) {
                index += backward ? -1 : 1;
            }
            if (index >= prefix.length && index < input.value.length && /[0-9]/.test(input.value[index])) {
                const digitsBeforeCaret = digitsOnly(input.value.slice(0, backward ? index : caret)).length;
                const digits = digitsOnly(input.value.slice(0, index) + input.value.slice(index + 1));

                input.value = formatDigits(digits);
                restoreCaret(digitsBeforeCaret);
                updateButtons(normalize(input.value));
            }
        }
    });
    input.addEventListener('input', formatDraft);
    input.addEventListener('paste', (event) => {
        event.preventDefault();
        const digits = digitsOnly(event.clipboardData.getData('text'));

        if (digits) {
            input.setRangeText(digits, input.selectionStart, input.selectionEnd, 'end');
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });
    input.addEventListener('blur', () => commit());
    input.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            input.blur();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            increment(event.key === 'ArrowUp' ? 1 : -1);
        }
    });
    decrease.addEventListener('click', () => increment(-1));
    increase.addEventListener('click', () => increment(1));

    commit();
}

export function initCalculator() {
    document.querySelectorAll(sectionSelector).forEach((section) => {
        const values = {};
        const result = section.querySelector('[data-calculator-result]');
        const output = section.querySelector('[data-calculator-output]');

        function updateResult() {
            const handling = section.querySelector('input[type="radio"]:checked')?.value;
            const percent = estimateSavings(values, handling);

            if (result) {
                result.textContent = percent;
            }
            if (output) {
                output.setAttribute('aria-label', percent + '%');
            }
        }

        section.querySelectorAll(fieldSelector).forEach((field) => {
            initField(field, (name, state) => {
                values[name] = state;
                updateResult();
            });
        });
        section.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.addEventListener('change', updateResult);
        });
    });
}
